/**
 * AuoSearchBox — the ONE auto-search picker that replaced the category-first
 * AUO selector (owner ask). Staff type a few letters and matching AUOs from ALL
 * categories (Activity / Utility / Object / Colour) appear at once, each row
 * carrying a small category tag — there is NO category pre-filter to choose.
 * Picking a row sets the name AND derives the `type` from that palette entry. A
 * free-typed value with no match stays as a custom name (inline-add) on blur.
 *
 * SHARED PRIMITIVE (extracted 2026-06-30): originally lived inside
 * AuoReviewPanel (the left-side staff list). The eval-workspace right-pane AUO
 * naming card reuses the SAME box rather than duplicating the typeahead logic,
 * so the keyboard model (↑/↓/Enter/Esc), outside-click close, category tags and
 * inline-add-on-blur stay in lockstep across both surfaces.
 *
 * Pure-white field, dark ink, border-only distinction (Fix 1 + pure-white
 * policy). Keyboard: ↑/↓ to move, Enter to pick the highlighted row, Esc closes.
 * The literal `#1a1a1a` (== --ink) is a hard fallback so the typed text can NEVER
 * inherit a light colour and go white-on-white if a var fails to resolve.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { searchPalette, type AuoPaletteItem } from '../lib/auoPalette';

/** Pure-white field on a pure-white panel: distinction via border + DARK ink. */
const fieldStyle: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '6px 8px',
  fontSize: 12.5,
  fontFamily: 'inherit',
  color: 'var(--ink, #1a1a1a)',
  background: '#FFFFFF',
  width: '100%',
};

/** Small category colour for a result row's tag (verbatim from the original
 *  left-list picker). The AA-verified Iris tokens — each category has its OWN hue
 *  (all ≥5:1 on white); Colour gets its own token (rose) rather than borrowing
 *  Object's indigo. */
function typeColour(type: string): string {
  switch (type) {
    case 'Activity':
      return 'var(--tag-activity)';
    case 'Utility':
      return 'var(--tag-utility)';
    case 'Colour':
      return 'var(--tag-colour)';
    default:
      return 'var(--tag-object)'; // Object
  }
}

export default function AuoSearchBox({
  value,
  type,
  paletteItems,
  onPick,
  onTypeText,
  onCommitCustom,
  ariaLabel = 'Search AUO across all categories',
  placeholder = 'Search AUO — type to find across Activity · Utility · Object',
  autoFocus = false,
}: {
  value: string;
  type: string;
  paletteItems: AuoPaletteItem[];
  onPick: (item: AuoPaletteItem) => void;
  onTypeText: (text: string) => void;
  onCommitCustom: (text: string) => void;
  /** override the input's accessible name (the right-pane card labels it "AUO name"). */
  ariaLabel?: string;
  placeholder?: string;
  /** WCAG 2.4.3 — move focus to the field when it mounts (the naming card case). */
  autoFocus?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // stable ids so the combobox can point aria-activedescendant at the active
  // option (WCAG 4.1.2 — arrow movement is announced to screen readers).
  const listboxId = useId();
  const optionId = (i: number) => `${listboxId}-opt-${i}`;

  // cap at 8 so the popover always sizes to its content with NO inner scrollbar
  // (owner's hard no-nested-scroll rule — a transient typeahead is no exception).
  const results = useMemo(
    () => searchPalette(paletteItems, value, 8),
    [paletteItems, value],
  );
  // an exact label match means the current value IS a real palette pick.
  const exact = useMemo(
    () =>
      paletteItems.some(
        (it) => it.label.toLowerCase() === value.trim().toLowerCase(),
      ),
    [paletteItems, value],
  );

  // move focus onto the field when the card mounts (keyboard + SR land here).
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // close the dropdown on an outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (item: AuoPaletteItem) => {
    onPick(item);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        style={fieldStyle}
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onTypeText(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          // a free-typed value not in the palette → record it for admin review.
          const v = e.target.value.trim();
          if (v && !exact) onCommitCustom(v);
        }}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            setOpen(true);
            return;
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, results.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === 'Enter') {
            if (open && results[active]) {
              e.preventDefault();
              pick(results[active]);
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        aria-controls={open && results.length > 0 ? listboxId : undefined}
        aria-activedescendant={open && results[active] ? optionId(active) : undefined}
      />
      {open && results.length > 0 && (
        <ul
          role="listbox"
          id={listboxId}
          style={{
            position: 'absolute',
            zIndex: 20,
            top: '100%',
            left: 0,
            right: 0,
            margin: '2px 0 0',
            padding: 4,
            listStyle: 'none',
            background: '#FFFFFF',
            border: '1px solid var(--rule-strong)',
            borderRadius: 'var(--r-sm)',
            boxShadow: '0 6px 18px rgba(30,26,21,0.12)',
            // no maxHeight / overflow — capped at 8 results, the popover sizes to
            // content with NO inner scrollbar (owner's hard no-nested-scroll rule).
          }}
        >
          {results.map((it, i) => (
            <li
              key={`${it.type}:${it.id || it.label}`}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              // mousedown (not click) so it fires BEFORE the input blur.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(it);
              }}
              onMouseEnter={() => setActive(i)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                padding: '6px 8px',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 12.5,
                color: 'var(--ink, #1a1a1a)',
                background: i === active ? 'var(--inset, #FAF8F4)' : 'transparent',
              }}
            >
              <span>{it.label}</span>
              <span
                style={{
                  flexShrink: 0,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  padding: '1px 6px',
                  borderRadius: 999,
                  color: typeColour(it.type),
                  border: `1px solid ${typeColour(it.type)}`,
                }}
              >
                {it.type}
              </span>
            </li>
          ))}
        </ul>
      )}
      {open && value.trim() && results.length === 0 && (
        <div
          style={{
            position: 'absolute',
            zIndex: 20,
            top: '100%',
            left: 0,
            right: 0,
            margin: '2px 0 0',
            padding: '8px 10px',
            background: '#FFFFFF',
            border: '1px solid var(--rule-strong)',
            borderRadius: 'var(--r-sm)',
            boxShadow: '0 6px 18px rgba(30,26,21,0.12)',
            fontSize: 11.5,
            color: 'var(--ink-mute, #8A8276)',
          }}
        >
          No match — “{value.trim()}” will be saved as a custom {type} for admin
          review.
        </div>
      )}
    </div>
  );
}
